import { useCallback, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import {
  fetchEmployeeCvDocuments,
  getMissingRequiredDocuments,
} from '../../utils/cvDocuments';

// Hatırlatmalar HER GİRİŞTE bir kez çıkar.
//
// Anahtar yalnızca sekmeye değil, oturumun kendisine bağlıdır: Firebase'in
// `lastSignInTime` damgası her yeni girişte değiştiği için çıkış yapıp tekrar
// giren kullanıcı uyarıyı yeniden görür. Aynı oturumda sayfa gezerken damga
// sabit kaldığından uyarı tekrar açılmaz ve Firestore'a tekrar sorulmaz.
const CHECK_KEY_PREFIX = 'missingCvDocumentsChecked';
export const SOP_PROMPT_SESSION_KEY = 'unreadSopPromptShown';

const sessionKeyFor = (user) =>
  `${CHECK_KEY_PREFIX}:${user?.uid || 'anon'}:${
    user?.metadata?.lastSignInTime || 'session'
  }`;

export const readSessionFlag = (key) => {
  try {
    return Boolean(sessionStorage.getItem(key));
  } catch (e) {
    // sessionStorage kapalıysa hatırlatma her açılışta gösterilir
    return false;
  }
};

export const writeSessionFlag = (key) => {
  try {
    sessionStorage.setItem(key, '1');
  } catch (e) {
    // yoksay
  }
};

// İşaretleri sil: hatırlatmalar yeniden gösterilir. Asıl sıfırlama GİRİŞ anında
// (LoginPage) yapılır; çıkışta Layout, auth değişimi efekti çalışmadan
// kaldırılabildiği için çıkıştaki temizlik tek başına güvenilir değildir.
export const resetLoginReminders = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(CHECK_KEY_PREFIX) || key === SOP_PROMPT_SESSION_KEY)
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (e) {
    // sessionStorage kapalıysa yapacak bir şey yok
  }
};

/**
 * Giriş yapan çalışanın eksik zorunlu CV/GCP/IATA belgelerini oturum başına
 * bir kez kontrol eder. `settled` kontrol sonuçlanınca (eksik olsun olmasın)
 * true olur; `markShown` kullanıcı hatırlatmayı kapattığında çağrılır.
 */
const useMissingDocuments = () => {
  const { employeeId, isLoading } = useUserProfile();
  const [missing, setMissing] = useState([]);
  const [settled, setSettled] = useState(false);
  // Giriş/çıkış geçişlerini yakalamak için auth kullanıcısı izlenir; böylece
  // yeni girişte efekt yeniden çalışır ve yeni oturum anahtarı üretilir.
  const [authUser, setAuthUser] = useState(() => auth.currentUser);

  useEffect(() => onAuthStateChanged(auth, setAuthUser), []);

  useEffect(() => {
    if (!authUser) {
      setMissing([]);
      setSettled(false);
      resetLoginReminders();
      return;
    }
    if (isLoading || !employeeId) return;

    if (readSessionFlag(sessionKeyFor(authUser))) {
      setSettled(true);
      return;
    }

    let cancelled = false;
    const checkDocuments = async () => {
      try {
        const documents = await fetchEmployeeCvDocuments(employeeId);
        if (cancelled) return;

        const missingTypes = getMissingRequiredDocuments(documents);
        // Eksik yoksa gösterilecek bir şey de yok: tekrar sorma
        if (missingTypes.length === 0) writeSessionFlag(sessionKeyFor(authUser));
        setMissing(missingTypes);
      } catch (error) {
        // Belgeler okunamazsa kullanıcıyı yanlış uyarmaktansa sessiz kal
        console.error('CV/GCP/IATA belge kontrolü başarısız:', error);
      }
      if (!cancelled) setSettled(true);
    };

    checkDocuments();
    return () => {
      cancelled = true;
    };
  }, [authUser, employeeId, isLoading]);

  const markShown = useCallback(() => {
    if (authUser) writeSessionFlag(sessionKeyFor(authUser));
  }, [authUser]);

  return { missing, settled, signedIn: Boolean(authUser), markShown };
};

export default useMissingDocuments;
